import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Widget } from '../../../../components/Widget/Widget';
import axios from 'Src/helpers/api/axiosInstance';

interface Tip {
  id: number | string;
  title: string;
  activeDate?: string;
  endDate?: string;
  priority?: number;
  content: string;
  ctaHref?: string;
  ctaText?: string;
  iconKey?: string;
}

type TipOfTheDayWidgetProps = {
  onRemove: () => void;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
};

function renderWidgetIcon(iconKey?: string): React.ReactNode {
  switch (String(iconKey || '').trim()) {
    case 'bell':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M6 9a6 6 0 1 1 12 0c0 4.5 1.5 5.5 2 6H4c.5-.5 2-1.5 2-6" />
          <path d="M10 19a2 2 0 0 0 4 0" />
        </svg>
      );

    case 'trophy':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M8 4h8v3a4 4 0 0 1-8 0V4z" />
          <path d="M8 5H5a2 2 0 0 0 2 4h1" />
          <path d="M16 5h3a2 2 0 0 1-2 4h-1" />
          <path d="M12 11v4" />
          <path d="M9 20h6" />
          <path d="M8 20h8" />
        </svg>
      );

    case 'badge':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="8" r="4" />
          <path d="M9.5 12.5 8 20l4-2 4 2-1.5-7.5" />
        </svg>
      );

    case 'media':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect x="4" y="5" width="16" height="14" rx="2" />
          <circle cx="9" cy="10" r="1.5" />
          <path d="m20 15-4.5-4.5L8 18" />
        </svg>
      );

    case 'rocket':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M14 4c3 1 5 3 6 6-2.5 2.5-5.5 4.5-9 6-1.5-3.5-3.5-6.5-6-9 3-3 6-4 9-3Z" />
          <path d="M10 14 6 18" />
          <path d="M8 16l-2 2" />
        </svg>
      );

    case 'home':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4 11.5 12 5l8 6.5" />
          <path d="M6.5 10.5V19h11v-8.5" />
        </svg>
      );

    case 'chart':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M5 19V9" />
          <path d="M12 19V5" />
          <path d="M19 19v-7" />
          <path d="M4 19h16" />
        </svg>
      );

    case 'megaphone':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M3 11v2" />
          <path d="M6 10h3l8-4v12l-8-4H6z" />
          <path d="M6 14l1.5 4h2" />
        </svg>
      );

    case 'search':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="11" cy="11" r="6" />
          <path d="m20 20-4.2-4.2" />
        </svg>
      );

    case 'clipboard':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect x="6" y="5" width="12" height="15" rx="2" />
          <path d="M9 5.5h6" />
          <path d="M9 10h6" />
          <path d="M9 14h4" />
        </svg>
      );

    case 'note':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M7 4h10l3 3v13H7z" />
          <path d="M17 4v4h4" />
          <path d="M10 12h6" />
          <path d="M10 16h4" />
        </svg>
      );

    case 'flag':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M6 21V4" />
          <path d="M6 5h10l-2 3 2 3H6" />
        </svg>
      );

    case 'check':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="m5 12 4 4 10-10" />
        </svg>
      );

    case 'list':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M9 7h10" />
          <path d="M9 12h10" />
          <path d="M9 17h10" />
          <circle cx="5" cy="7" r="1" />
          <circle cx="5" cy="12" r="1" />
          <circle cx="5" cy="17" r="1" />
        </svg>
      );

    case 'calendar':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M8 3v3M16 3v3M4 9h16" />
          <rect x="4" y="5" width="16" height="15" rx="3" ry="3" />
        </svg>
      );

    case 'status':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="6" cy="12" r="2" />
          <circle cx="12" cy="12" r="2" />
          <circle cx="18" cy="12" r="2" />
        </svg>
      );

    case 'shirt':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M9 5 12 7l3-2 4 3-2 3-2-1v9H9v-9l-2 1-2-3 4-3z" />
        </svg>
      );

    default:
      return null;
  }
}

export const TipOfTheDayWidget: React.FC<TipOfTheDayWidgetProps> = ({ onRemove, dragHandleProps }) => {
  const [tip, setTip] = useState<Tip | null>(null);
  const history = useHistory();
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = (await axios.get('/dist/images/tips.json')) as { data: Tip[] };
        const today = new Date().toISOString().split('T')[0];
        const active = data.filter(t => (t.activeDate || '0000-01-01') <= today && today <= (t.endDate || '9999-12-31'));
        const newestDate = active.reduce((latest, item) => item.activeDate && item.activeDate > latest ? item.activeDate : latest, '0000-01-01');
        const newest = active.filter(item => (item.activeDate || '0000-01-01') === newestDate);
        const highestPriority = newest.reduce((highest, item) => Math.max(highest, item.priority || 0), 0);
        const featured = newest.filter(item => (item.priority || 0) >= highestPriority - 20);
        const pool = featured.length ? featured : newest.length ? newest : data;
        setTip(pool[Math.floor(Math.random() * pool.length)]);
      } catch (e) {
        console.error('Error fetching tips:', e);
      }
    })();
  }, []);

  const onHtmlClick: React.MouseEventHandler<HTMLDivElement> = e => {
    const anchor = (e.target as HTMLElement).closest('a') as HTMLAnchorElement | null;
    if (!anchor) return;

    const href = anchor.getAttribute('href') || '';
    const isExternal = /^https?:\/\//i.test(href) || anchor.target === '_blank';
    if (!href) return;

    if (isExternal) return;

    if (href.startsWith('/')) {
      e.preventDefault();
      e.stopPropagation();
      history.push(href);
    }
  };

  return (
    <Widget title="Tip of the Day" onRemove={onRemove} dragHandleProps={dragHandleProps}>
      <div className="tip-widget">
        {tip ? (
          <div className="tip-card">
            <div className="tip-cardGlow" aria-hidden="true" />

            <div className="tip-cardHeader">
              <div className="tip-cardEyebrowWrap">
                {tip.iconKey ? <span className="tip-cardIcon">{renderWidgetIcon(tip.iconKey)}</span> : null}
                <div className="tip-cardEyebrow">Daily tip</div>
              </div>

              <h4 className="tip-title">{tip.title}</h4>
            </div>

            <div
              ref={contentRef}
              className="tip-content"
              onClick={onHtmlClick}
              dangerouslySetInnerHTML={{ __html: tip.content }}
            />

            {tip.ctaHref && (
              <div className="tip-cardFooter">
                <button type="button" className="primary-cta-btn tip-cta-btn" onClick={() => history.push(tip.ctaHref!)}>
                  {tip.ctaText || 'Learn more'}
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="tip-widgetEmpty">Loading tip...</div>
        )}
      </div>
    </Widget>
  );
};
